import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { NavbarDashbord } from './navbarDashbord';
import { Plus, Edit2, Trash2, Search, Wind, Ban, MessageSquare, Info, Star, User, ThumbsUp, ThumbsDown } from 'lucide-react';
import { deleteClimatisseur } from './action';
import '../styles/admin.css';

export function PageClimatisseurs() {
    const listC = useSelector(state => state.listC) || [];
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [selectedClim, setSelectedClim] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("Tous");

    const closeModal = () => setSelectedClim(null);

    const handleSupprimer = (id) => {
        if (window.confirm("Voulez-vous vraiment supprimer cet appareil ? Cette action est irréversible.")) {
            dispatch(deleteClimatisseur(id));
        }
    };

    // Helper to get stock and status
    const getStockData = (ac) => {
        const count = (ac.stock !== undefined && ac.stock !== null) ? ac.stock : (parseInt(ac.id) * 7 % 25);
        let status = "En Stock";
        if (count === 0) status = "Rupture";
        else if (count < 5) status = "Limité";
        return { count, status };
    };

    // Extract dynamic unique statuses from the actual list
    const uniqueStatuses = ["Tous", ...new Set(listC.map(ac => getStockData(ac).status))];

    // Real-time filtering logic
    const filteredList = listC.filter(ac => {
        const { count, status } = getStockData(ac);
        
        const term = searchTerm.toLowerCase().trim();
        const matchesSearch = term === "" || 
            ac.nomClimatisseur.toLowerCase().includes(term) ||
            ac.markClimatisseur.toLowerCase().includes(term);

        const matchesStatus = filterStatus === "Tous" || status === filterStatus;

        return matchesSearch && matchesStatus;
    });

    return (
        <div className="admin-layout">
            <NavbarDashbord />
            <main className="admin-main-content">
                <header className="admin-header">
                    <div className="admin-title-group">
                        <h1>Gestion des Climatiseurs</h1>
                        <p>Visualisez et gérez votre catalogue de {listC.length} climatiseurs.</p>
                    </div>
                </header>

                <div className="admin-table-container">
                    <div className="table-header-actions">
                        <div className="admin-filters">
                            <select
                                className="admin-select"
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                            >
                                {uniqueStatuses.map((status, idx) => (
                                    <option key={idx} value={status}>
                                        {status === "Tous" ? "Statut: Tous" : status}
                                    </option>
                                ))}
                            </select>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    placeholder="Rechercher un modèle ou marque..."
                                    className="admin-select"
                                    style={{ paddingLeft: '40px', width: '300px' }}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-text-muted)' }} />
                            </div>
                        </div>
                        <button className="admin-btn-add" onClick={() => navigate('/admin-climatisseur/add')}>
                            <Plus size={20} />
                            <span>Ajouter un Climatisseur</span>
                        </button>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th style={{ minWidth: '250px', width: '35%' }}>Produit</th>
                                    <th>Marque</th>
                                    <th>Tailles & Prix</th>
                                    <th>Stock</th>
                                    <th>Avis</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredList.map((ac) => {
                                    const { count: stockCount, status: stockStatus } = getStockData(ac);
                                    const statusColor = stockStatus === "Rupture" ? "var(--admin-danger)" : stockStatus === "Limité" ? "var(--admin-warning)" : "var(--admin-success)";

                                    return (
                                        <tr key={ac.id}>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                                    <div className="table-img-wrapper" style={{ width: '65px', height: '65px', borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--admin-border)', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', flexShrink: 0 }}>
                                                        <img src={ac.image} alt={ac.nomClimatisseur} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    </div>
                                                    <div>
                                                        <p style={{ margin: 0, fontWeight: '900', fontSize: '1.05rem', color: 'var(--admin-primary)', marginBottom: '4px', lineHeight: '1.2', maxWidth: '200px' }}>{ac.nomClimatisseur}</p>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', whiteSpace: 'nowrap' }}>
                                                            <span style={{ fontSize: '0.7rem', color: 'var(--admin-text-muted)', fontWeight: '500' }}>Mise à jour : Aujourd'hui</span>
                                                            <span style={{ fontSize: '0.65rem', color: 'var(--admin-text-muted)', fontWeight: '500' }}>ID : #{ac.id}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="admin-badge" style={{ backgroundColor: '#f1f5f9', color: 'var(--admin-primary)', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                                    {ac.markClimatisseur}
                                                </span>
                                            </td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '150px' }}>
                                                {ac.tailles && ac.tailles.length > 0 ? (
                                                    ac.tailles.map((t, i) => (
                                                        <div key={i} style={{ fontSize: '0.8rem', fontWeight: '700', padding: '4px 8px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #f1f5f9' }}>
                                                            <span style={{ color: 'var(--admin-secondary)' }}>{t.taille} :</span> {t.prix.toLocaleString()} MAD
                                                        </div>
                                                    ))
                                                ) : <span style={{ color: 'var(--admin-text-muted)' }}>N/A</span>}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="admin-badge" style={{
                                                backgroundColor: `${statusColor}15`,
                                                color: statusColor,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                fontSize: '0.7rem'
                                            }}>
                                                <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: statusColor }}></div>
                                                {stockStatus} ({stockCount})
                                            </span>
                                        </td>
                                        <td>
                                            <Link
                                                to={`/pageCommentaires?climatiseurId=${ac.id}`}
                                                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '8px', background: 'rgba(112, 0, 0, 0.05)', width: 'fit-content' }}
                                            >
                                                <MessageSquare size={16} color="var(--admin-secondary)" />
                                                <span style={{ fontWeight: '800', color: 'var(--admin-primary)' }}>{ac.commantaires ? ac.commantaires.length : 0}</span>
                                            </Link>
                                        </td>
                                        <td>
                                            <div className="action-btns">
                                                <button className="btn-icon" title="Plus d'infos" onClick={() => setSelectedClim({ ...ac, currentStock: stockCount, currentStatus: stockStatus, statusColor })}>
                                                    <Info size={16} />
                                                </button>
                                                <button className="btn-icon btn-modify" title="Modifier" onClick={() => navigate(`/admin-climatisseur/edit/${ac.id}`)}>
                                                    <Edit2 size={16} />
                                                </button>
                                                <button className="btn-icon btn-delete" title="Supprimer" onClick={() => handleSupprimer(ac.id)}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredList.length === 0 && (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'var(--admin-text-muted)' }}>
                                        Aucun climatiseur ne correspond à votre recherche ou filtre.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        </table>
                    </div>
                </div>

                {/* Info Modal */}
                {selectedClim && (
                    <div className="admin-modal-overlay" onClick={closeModal}>
                        <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                    <div style={{ width: '80px', height: '80px', borderRadius: '15px', overflow: 'hidden', border: '1px solid #eee' }}>
                                        <img src={selectedClim.image} alt={selectedClim.nomClimatisseur} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    </div>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <h2 style={{ margin: 0, color: 'var(--admin-primary)' }}>{selectedClim.nomClimatisseur}</h2>
                                            <span style={{ backgroundColor: `${selectedClim.statusColor}15`, color: selectedClim.statusColor, padding: '4px 10px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '800' }}>
                                                {selectedClim.currentStatus} ({selectedClim.currentStock})
                                            </span>
                                        </div>
                                        <p style={{ margin: '5px 0 0', color: 'var(--admin-secondary)', fontWeight: '700' }}>Marque: {selectedClim.markClimatisseur}</p>
                                    </div>
                                </div>
                                <button className="modal-close-btn" onClick={closeModal}>&times;</button>
                            </div>

                            <div className="modal-body">
                                <div className="modal-section">
                                    <h3>Description</h3>
                                    <p style={{ lineHeight: '1.6', fontSize: '0.9rem', color: '#444' }}>{selectedClim.description}</p>
                                </div>

                                <div className="modal-section">
                                    <h3>Spécifications par Taille</h3>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {selectedClim.tailles && selectedClim.tailles.map((t, i) => (
                                            <div key={i} className="spec-card" style={{ width: '95%', margin: '0 auto', border: '1px solid #eee', borderRadius: '12px', background: '#fff', overflow: 'hidden' }}>
                                                <div className="spec-card-header" style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 15px', background: '#000033', borderBottom: '1px solid #eee' }}>
                                                    <span style={{ fontWeight: '800', color: 'var(--admin-secondary)', fontSize: '0.9rem' }}>{t.taille}</span>
                                                    <strong style={{ fontSize: '0.9rem' }}>{t.prix.toLocaleString()} MAD</strong>
                                                </div>
                                                <div className="spec-card-body" style={{ padding: '15px' }}>
                                                    <div className="spec-item" style={{ marginBottom: '15px', fontSize: '0.85rem' }}>
                                                        <span style={{ color: 'var(--admin-text-muted)' }}>Dimensions: </span>
                                                        <strong>{t.details.longueur} x {t.details.largeur} x {t.details.somk}</strong>
                                                    </div>
                                                    <div className="tech-props" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                        {t.proprietesTechniques && t.proprietesTechniques.map((p, idx) => {
                                                            let displayValue = p.valeur;
                                                            if (p.propriete.toLowerCase() === "fonctionnalites" && p.valeur && typeof p.valeur === 'string') {
                                                                displayValue = p.valeur.split(' , ').join(' - ');
                                                            }
                                                            return (
                                                                <div key={idx} className="tech-prop-badge" style={{ fontSize: '0.75rem', background: '#fff', padding: '5px 12px', borderRadius: '6px', border: '1px solid #eee', display: 'flex', gap: '5px' }}>
                                                                    <span className="prop-name" style={{ fontWeight: '700', color: 'var(--admin-primary)' }}>{p.propriete}:</span>
                                                                    <span className="prop-val">{displayValue}</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="modal-section">
                                    <h3>Les couleurs disponibles</h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' }} className='couleur-modal'>
                                        {selectedClim.colors && selectedClim.colors.map((c, i) => (
                                            <div key={i} style={{ border: '1px solid #eee', borderRadius: '12px', overflow: 'hidden', textAlign: 'center', background: '#fff' }}>
                                                <div style={{ height: '120px', background: '#f9f9f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <img src={c.image} alt={c.color} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                                </div>
                                                <div style={{ padding: '10px', fontSize: '0.85rem', fontWeight: '700', borderTop: '1px solid #eee' }}>
                                                    {c.color}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="modal-section">
                                    <h3>Les commentaires</h3>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                        {(!selectedClim.commantaires || selectedClim.commantaires.length === 0) ? (
                                            <p style={{ color: 'var(--admin-text-muted)', fontSize: '0.9rem', textAlign: 'center', padding: '20px' }}>Aucun commentaire pour le moment.</p>
                                        ) : (
                                            selectedClim.commantaires.map((com, i) => (
                                                <div key={i} style={{ display: 'flex', background: '#f8fafc', borderRadius: '12px', border: '1px solid #eee', overflow: 'hidden', marginBottom: '15px', boxShadow: '0 2px 5px rgba(0,0,0,0.02)', position: 'relative' }}>
                                                    {/* Left Side: 40% - Dictates Card Height */}
                                                    <div style={{ width: '33%', padding: '15px', display: 'flex', flexDirection: 'column', borderRight: '1px solid #eee' }}>
                                                        {/* Row 1: Username and Stars */}
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                            <h4 style={{ margin: 0, fontSize: '0.85rem', fontWeight: '800', color: 'var(--admin-secondary)' }}>{com.username}</h4>
                                                            <div style={{ display: 'flex', gap: '2px' }} className='star-comments'>
                                                                {[...Array(5)].map((_, idx) => (
                                                                    <Star 
                                                                        key={idx} 
                                                                        size={10} 
                                                                        fill={idx < parseInt(com.avis) ? "#ffc107" : "none"} 
                                                                        color={idx < parseInt(com.avis) ? "#ffc107" : "#ccc"} 
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>

                                                        {/* Separator Line */}
                                                        <div style={{ width: '85%', height: '1px', backgroundColor: '#eee', margin: '2px auto 8px' }}></div>
                                                        
                                                        {/* Row 2: Comment Text */}
                                                        <div style={{ marginBottom: '10px' }}>
                                                            <p style={{ margin: 0, fontSize: '0.8rem', lineHeight: '1.4', color: '#444' }}>{com.commantaire}</p>
                                                        </div>

                                                        {/* Row 3: Professional Like/Dislike Buttons */}
                                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: 'auto' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '3px 8px', borderRadius: '20px', border: '1px solid #eee', background: '#fff', fontSize: '0.7rem', fontWeight: '700', color: '#10b981' }}>
                                                                <ThumbsUp size={12} />
                                                                <span>{com.like || 0}</span>
                                                            </div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 8px', borderRadius: '20px', border: '1px solid #eee', background: '#fff', fontSize: '0.7rem', fontWeight: '700', color: '#ef4444' }}>
                                                                <ThumbsDown size={12} />
                                                                <span>{com.dislike || 0}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Right Side: 60% - Absolute Position (Matches Left side height) */}
                                                    <div style={{ width: '60%', padding: '15px', overflowY: 'auto', fontSize: '0.7rem', background: '#fff', position: 'absolute', right: 0, top: 0, bottom: 0 }}>
                                                        <div style={{ marginBottom: '15px' }}>
                                                            <strong style={{ color: 'var(--admin-primary)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px', borderBottom: '1px solid #f0f0f0', paddingBottom: '4px' }}>Aimé par:</strong>
                                                            {com.likedBy && com.likedBy.length > 0 ? (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                    {com.likedBy.map((email, idx) => {
                                                                        const isAdmin = email.toLowerCase().includes('admin');
                                                                        return (
                                                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '1px 0' }}>
                                                                                <User size={10} style={{ color: '#64748b' }} />
                                                                                <span style={{ color: '#334155', fontWeight: '500' }}>{email}</span>
                                                                                <span style={{ 
                                                                                    fontSize: '0.55rem', 
                                                                                    background: isAdmin ? 'rgba(112, 0, 0, 0.1)' : 'rgba(51, 65, 85, 0.1)', 
                                                                                    color: isAdmin ? 'var(--admin-secondary)' : '#334155',
                                                                                    padding: '1px 5px',
                                                                                    borderRadius: '4px',
                                                                                    fontWeight: '800',
                                                                                    textTransform: 'uppercase'
                                                                                }}>
                                                                                    {isAdmin ? 'Admin' : 'Client'}
                                                                                </span>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : <span style={{ color: '#ccc', fontStyle: 'italic' }}>Aucun vote...</span>}
                                                        </div>
                                                        
                                                        <div>
                                                            <strong style={{ color: 'var(--admin-secondary)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px', borderBottom: '1px solid #f0f0f0', paddingBottom: '4px' }}>Désapprouvé par:</strong>
                                                            {com.dislikedBy && com.dislikedBy.length > 0 ? (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                    {com.dislikedBy.map((email, idx) => {
                                                                        const isAdmin = email.toLowerCase().includes('admin');
                                                                        return (
                                                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '1px 0' }}>
                                                                                <User size={10} style={{ color: '#64748b' }} />
                                                                                <span style={{ color: '#334155', fontWeight: '500' }}>{email}</span>
                                                                                <span style={{ 
                                                                                    fontSize: '0.55rem', 
                                                                                    background: isAdmin ? 'rgba(112, 0, 0, 0.1)' : 'rgba(51, 65, 85, 0.1)', 
                                                                                    color: isAdmin ? 'var(--admin-secondary)' : '#334155',
                                                                                    padding: '1px 5px',
                                                                                    borderRadius: '4px',
                                                                                    fontWeight: '800',
                                                                                    textTransform: 'uppercase'
                                                                                }}>
                                                                                    {isAdmin ? 'Admin' : 'Client'}
                                                                                </span>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : <span style={{ color: '#ccc', fontStyle: 'italic' }}>Aucun vote...</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

